import { useMemo } from "react"
import { useQuery, type UseQueryResult } from "@tanstack/react-query"
import { useMessagesHost, type MessagesProviderApi } from "../host/MessagesHostContext"
import { HttpStatusCodes } from "../lib/HttpStatusCodes"
import { PROVIDER_THREADS_CACHE_KEY } from "../lib/providerThreadsCache"
import { fromProviderThread, type UnifiedThread } from "../lib/unifiedThread"
import type { ProviderApiError, ProviderThreadList } from "../providerMessagingTypes"

// Re-exported so everything inside this package keeps taking the key off the hook that queries
// with it. Hosts import the leaf instead — see the note there and `exports` in package.json.
export { PROVIDER_THREADS_CACHE_KEY }

// How often each surface re-asks for the thread list. HTTP polling is still the only thing that
// gets a provider's reply into the tab without a reload — the SPA talks to the BFF and never to
// Convex, so there is no websocket and no live query to subscribe to — which is why these two
// numbers are the feature's whole liveness story.
//
// They are TUNED, where their predecessors (30s / 120s) could only be guesses. A poll used to reach
// Healthie live, and Healthie publishes no requests-per-minute figure, only that limits exist and
// are "subject to dynamic adjustment" — there was no ceiling to divide up, so the rates were picked
// to feel right and stay well clear of one. A poll is now a Convex query against the mirror (see
// packages/convex/src/healthie/messages.ts) and reaches Healthie never, so what a cycle costs is one
// BFF invocation and one indexed Convex read per member. That is the budget these spend.
//
// The mirror is fed by webhooks, so a reply is in Convex moments after it is sent and the poll
// interval is the whole of the member's remaining latency. The two rates are what that latency
// BUYS on each surface: the nav dot only has to light a 7px dot, so half a minute of staleness
// costs the member nothing they would notice.
export const NAV_POLL_INTERVAL_MS = 30_000
// The open inbox should feel live: the member is looking at the list a reply lands in.
export const INBOX_POLL_INTERVAL_MS = 5_000

/**
 * The provider thread list, with the transport and the poll rate passed IN rather than read off the
 * host context.
 *
 * Split out because `useProviderUnreadCount` runs in the app's main nav, outside
 * `<MessagesHostProvider>`, and needs exactly one method from the contract — standing up the whole
 * contract for a 7px dot would pull the panel into the nav's bundle. Cache key, `staleTime` and the
 * retry switch are therefore defined HERE, once — the dot and the panel then share one cache entry,
 * so opening the Providers tab does not start a second stream of provider requests alongside the
 * nav's, and neither surface can drift from the other's retry policy. (It costs at most ONE request:
 * the `staleTime` is a poll cycle, so a navigation lands on cached rows or refreshes them, never
 * both.)
 *
 * `pollIntervalMs` is a parameter and not one constant BECAUSE of that shared entry. The dot and the
 * panel want different rates out of the same data, and react-query v4 keeps its refetch timer per
 * OBSERVER rather than per query: while the Providers tab is open both timers run against the one
 * entry, both fire, and in-flight dedupe collapses any overlap into a single request. So the effective
 * rate with both mounted is the faster of the two, not their sum — which reads like a bug in a network
 * log until you know that is the intended arrangement.
 *
 * An absent `getThreads` means the CALLER decided not to ask — the nav dot for a member who is not a
 * consumer, and nothing else: the host contract's `providerApi` is required. The query is disabled and
 * nothing is fetched, timer included. Note that a disabled react-query sits at `status: "loading"`
 * forever, so callers must not read `isLoading` off the result without first checking they passed a
 * transport in.
 */
export function useProviderThreadsQuery(
  getThreads: MessagesProviderApi["getThreads"] | undefined,
  pollIntervalMs: number
): UseQueryResult<ProviderThreadList, ProviderApiError> {
  return useQuery<ProviderThreadList, ProviderApiError>(
    PROVIDER_THREADS_CACHE_KEY,
    // Unpaged: the transport answers the member's whole inbox, so there is nothing to thread
    // through here.
    () => getThreads(),
    {
      enabled: getThreads != null,
      // ONE NUMBER WITH THE POLL RATE, deliberately, and expressed as the constant rather than as a
      // literal that happens to match it. `refetchInterval` fires regardless of staleness, so a
      // `staleTime` longer than the interval does not slow the timer down — it governs the OTHER
      // refetches, on mount and on window focus, and the pair used to be incoherent: 60s of freshness
      // against a 30s poll meant a member navigating back inside a minute was served rows up to a
      // minute old while the timer worked around them. At one cycle nothing is ever served older than
      // one cycle, and nothing asks more often than one cycle either.
      //
      // The fastest surface's rate and not `pollIntervalMs`, because the two surfaces share this
      // cache entry and react-query decides staleness PER OBSERVER: taking the parameter would let
      // the dot and the panel disagree about how old the one entry they both read is allowed to be.
      //
      // It also covers the gap the visibility-based timer leaves (see `refetchIntervalInBackground`
      // below): a backgrounded tab stops polling entirely, so the focus refetch is what a member
      // returning to it after ten minutes gets, immediately, instead of waiting out a cycle.
      staleTime: INBOX_POLL_INTERVAL_MS,
      // Function form, so react-query re-asks after every state change of this query rather than
      // freezing whatever the rate was at mount.
      refetchInterval: (data) => {
        // No Healthie patient record: there is nothing for a thread to appear IN, and one cannot
        // appear mid-session without a navigation that refetches anyway. Every poll here would be a
        // Vercel invocation spent to be told the same thing — the BFF's `withMemberIdentity`
        // short-circuits this member before any Convex call, so it is pure overhead, and it is most
        // consumer members.
        //
        // `=== false` and deliberately not `!data?.hasProviderIdentity`: `data` is undefined before
        // the first answer and while a data-less query retries, and reading "we don't know yet" as
        // "no record" would silence the timer for a member who has one.
        if (data?.hasProviderIdentity === false) {
          return false
        }
        // THAT IS THE ONLY BRANCH, and there used to be a second: a ten-minute backoff after a 429,
        // to stop the poll holding a Healthie throttle open on its own schedule. A poll cannot be
        // throttled any more — it is a Convex read and Healthie never sees it — so the branch was
        // unreachable on this path and went with the live read. It is NOT a relaxation of the rule:
        // 429 is still terminal in the retry switch below, still terminal in the SPA's tRPC policy
        // (packages/bff-client/src/retryPolicy.ts), and the writes that can still provoke one
        // (`sendMessage`, `markRead`) still go to Healthie. Only the POLL had nothing left to yield.
        return pollIntervalMs
      },
      // `refetchIntervalInBackground` is LEFT UNSET on purpose — please do not "fix" this by adding
      // it. The v4 default of `false` makes the timer visibility-based: the observer checks
      // `focusManager.isFocused()` before each tick, so a backgrounded tab stops polling outright and
      // the multi-tab case collapses to whichever tab the member is actually looking at. Setting it
      // true would multiply our request volume by every tab and every idle laptop left open.
      retry: (failureCount, error) => {
        switch (error?.statusCode) {
          case HttpStatusCodes.UNAUTHORIZED:
          case HttpStatusCodes.BAD_REQUEST:
          case HttpStatusCodes.NOT_FOUND:
          // A forbidden conversation and a closed one will never start succeeding, so retrying
          // either just delays the failed render.
          case HttpStatusCodes.FORBIDDEN:
          case HttpStatusCodes.CONFLICT:
          // 412 is the BFF reporting that this session has no Healthie identity. That is a
          // property of the session rather than of the attempt, so every retry gets the
          // same answer.
          case HttpStatusCodes.PRECONDITION_FAILED:
          // 429 is Healthie telling us to slow down. Unlike everything above it, retrying
          // would not just fail again — it would make the condition worse, spending the
          // member's remaining quota to be refused sooner.
          case HttpStatusCodes.TOO_MANY_REQUESTS:
            return false
          default:
            return failureCount <= 3
        }
      }
    }
  )
}

export type ProviderThreadsResult = {
  /** Sorted by `updatedAt` DESC. */
  threads: UnifiedThread[]
  /**
   * The FIRST load is in flight, so the list may still gain threads. Empty-and-loading and
   * empty-and-answered are different states to a caller: `useSelectedThread` keeps a deep link alive
   * across the first, and `ProviderMessagesPanel` will only drop an unresolvable key from the URL once
   * this is false.
   */
  isLoading: boolean
  /** Failed with nothing to show. */
  isError: boolean
  /**
   * Whether the member has a provider record AT ALL — read straight off the payload, and NOT derivable
   * from `threads` being empty: the two readings are different members and the panel shows them
   * different pages. See `ProviderThreadList`.
   *
   * `true` while the answer is unknown, because that is the reading that claims nothing: the panel's
   * own loading and error branches come first, and the one thing this must not do is tell a member
   * with a record that they have none for the frames before the list lands.
   *
   * "Unknown" means NO SUCCESSFUL ANSWER IS HELD — the first load, or a failure that arrived with no
   * data. It is deliberately NOT every failure. A poll that fails on top of a successful answer keeps
   * react-query's retained `data`, so this keeps reporting what that answer said, `false` included:
   * a member with no provider record is not told they might have one because the poll dropped a beat.
   * The panel reports the failure through `isError` in the same frame either way. See
   * `reports the failure of a refetch, without forgetting the identity it already knew`.
   */
  hasProviderIdentity: boolean
}

/**
 * Whether the query is loading for the FIRST time.
 *
 * `errorUpdateCount` and not just `isLoading`, and that is not a detail: react-query clears the error
 * and puts a query with no data back into `status: "loading"` on every retry (`case "fetch"` in
 * query-core's reducer), so `isLoading` alone reports a list that has ALREADY FAILED as loading again
 * each time it retries. Callers gate the selected thread on this, and a selection that flips resolved
 * -> waiting -> resolved unmounts and remounts the components observing this query — each of which
 * fetches again on mount, because a query in `error` state is always stale. That is an infinite fetch
 * loop, and it was a real one: 186 requests in the first second of a degraded render.
 */
function isFirstLoad(query: { isLoading: boolean; errorUpdateCount: number }): boolean {
  return query.isLoading && query.errorUpdateCount === 0
}

/**
 * Whether the query has failed and has nothing to show.
 *
 * NOT just `isError`, and the difference is a real frame rather than a theoretical one. The same
 * react-query behaviour `isFirstLoad` guards against — a data-less query returning to
 * `status: "loading"` around a retry, with `errorUpdateCount` already incremented — produces a window
 * in which a list that HAS failed reports neither `isLoading` nor `isError`. Anything gating on
 * "loading or failed" therefore sees a list that has answered successfully, which it has not. That
 * window is exactly long enough for the panel to conclude a deep-linked thread is unresolvable and
 * rewrite the URL — observed, not hypothesised.
 *
 * `data === undefined` is what keeps this from sticking: a query that failed once and then succeeded
 * on retry has data, and is not in error. A query holding stale data whose refetch failed reports
 * `isError` and is, since the member is looking at something we could not confirm.
 *
 * `data` is what the source resolved WHOLE — the `{ threads, hasProviderIdentity }` payload, not the
 * array inside it — so its presence means one thing and only one thing: a fetch succeeded. Do not
 * narrow this to the rows. An inbox with no threads in it is a successful answer, and reporting it as
 * an error would put the "couldn't be loaded" tile in front of every member who simply has no
 * provider conversations.
 */
function isDegraded(query: { isError: boolean; errorUpdateCount: number; data: unknown }): boolean {
  return query.isError || (query.errorUpdateCount > 0 && query.data === undefined)
}

/** Every provider conversation the member has, newest activity first. */
export function useProviderThreads(): ProviderThreadsResult {
  const { providerApi } = useMessagesHost()

  // Handing the method over detached from its object is safe: the contract's implementations are
  // closures, not `this`-dependent methods.
  //
  // The INBOX rate: this hook only runs while the Providers tab is open, which is the one place a
  // member watches for a reply. The nav dot asks the same cache entry for the same data at its own
  // slower rate; see `useProviderThreadsQuery` for why both timers running is fine.
  const query = useProviderThreadsQuery(providerApi.getThreads, INBOX_POLL_INTERVAL_MS)

  const threads = useMemo<UnifiedThread[]>(() => {
    // LOAD-BEARING, not cosmetic. The provider orders its own list by the membership's `updated_at`,
    // which moves when a thread is marked READ, so "newest first" over there is not
    // newest-activity-first.
    return (query.data?.threads ?? []).map(fromProviderThread).sort((left, right) => right.updatedAt - left.updatedAt)
  }, [query.data])

  const isLoading = isFirstLoad(query)
  const isError = isDegraded(query)
  // `!== false` rather than `?? true`, and the same rule as the poll's own branch above: only an
  // explicit `false` from a successful answer means "no record". `undefined` — no answer yet, or a
  // data-less failure — is not that.
  const hasProviderIdentity = query.data?.hasProviderIdentity !== false

  return useMemo<ProviderThreadsResult>(
    () => ({ threads, isLoading, isError, hasProviderIdentity }),
    [threads, isLoading, isError, hasProviderIdentity]
  )
}
